import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ToastrService } from 'ngx-toastr';
import { finalize, switchMap } from 'rxjs/operators';
import { PostgridCountryFilter, PostgridService } from '../../postgrid.service';

type ContactCreatorStep = 'company' | 'addresses' | 'people' | 'review';
type ThemeMode = 'dark' | 'light';
type AddressAssistStatus = 'idle' | 'manual' | 'filled' | 'error';

interface AddressSuggestion {
  label: string;
  street: string;
  extended: string;
  locality: string;
  region: string;
  postalCode: string;
  country: string;
  raw: unknown;
}

interface AddressState {
  form: FormGroup;
  suggestions: AddressSuggestion[];
  loading: boolean;
  status: AddressAssistStatus;
  message: string;
  fromPostgrid: boolean;
}

@Component({
  selector: 'app-contact-creator',
  templateUrl: './contact-creator.component.html',
  styleUrls: ['./contact-creator.component.scss']
})
export class ContactCreatorComponent implements OnInit {
  readonly contactUrl = '/api/contacts/contact_details_profiles.php?table=contacts';
  readonly contactTypesUrl = '/api/contacts/contact_types.php';
  readonly postgridLookupsEnabled = true;
  readonly themeStorageKey = 'contact-creator-theme';

  readonly steps: Array<{ key: ContactCreatorStep; label: string; icon: string }> = [
    { key: 'company', label: 'Company', icon: 'fa-building' },
    { key: 'addresses', label: 'Addresses', icon: 'fa-location-dot' },
    { key: 'people', label: 'People', icon: 'fa-address-book' },
    { key: 'review', label: 'Review', icon: 'fa-list-check' }
  ];

  readonly typeList = [
    'Main Office',
    'Branch Office',
    'Plant',
    'Warehouse',
    'Home',
    'Store',
    'Mailing',
    'Accounting',
    'Terminal',
    'Job Site',
    'Remittance Address',
    'Physical Address'
  ];

  readonly typeListContact = [
    'Default',
    'Manufacturer',
    'Service Provider',
    'Contractor',
    'Shipper',
    'Receiver Consignee',
    'Carrier',
    'Payment Processor',
    'Vendor',
    'Reseller',
    'Distributor',
    'Agent',
    'Friend',
    'Family',
    'Volunteer',
    'Company Division',
    'Driver',
    'APP Client',
    'Logistics Broker',
    'Customs Broker',
    'Customer Bill To',
    'Insurance Agent',
    'Prospect',
    'Hot Prospect',
    'Representative',
    'Business Reference',
    'Warehouse Terminal',
    'Technician'
  ];

  readonly typeListContactRole = [
    'Accounts Payable',
    'Accounts Receivable',
    'Billing',
    'Customer Support',
    'Development',
    'Director',
    'Dispatch',
    'Engineering',
    'Executive',
    'Finance',
    'Human Resources',
    'Logistics',
    'Marketing',
    'Operation',
    'Owner Operator',
    'Research',
    'Sales',
    'Shipping Receiving',
    'Store Manager',
    'District Manager',
    'Owner'
  ];

  readonly typeListPhone = [
    'Work',
    'Home',
    'Mobile',
    'Main',
    'Fax',
    'Pager',
    'Voicemail',
    'Text',
    'Toll Free',
    'Office',
    'After Hours',
    'Other'
  ];

  readonly typeListContactTitle = [
    'Accounts Manager',
    'Accounts Payable',
    'Accounts Receivable',
    'CEO',
    'CFO',
    'CTO',
    'CIO',
    'Chairman',
    'Customer Service Rep',
    'Department Head',
    'Division Chief',
    'Engineer',
    'President',
    'Manager',
    'Receptionist',
    'Sales Rep',
    'Senior Executive',
    'Technician',
    'VP'
  ];

  readonly typeListTrueFalse = ['True', 'False'];

  readonly typeListTimeZone = [
    'Pacific Time Zone',
    'Mountain Time Zone',
    'Central Time Zone',
    'Eastern Time Zone',
    'Atlantic Time Zone',
    'Newfoundland Time Zone'
  ];

  readonly typeListCountry = ['Canada', 'United States'];

  readonly provinceByCode: Record<string, string> = {
    AB: 'Alberta',
    BC: 'British Columbia',
    MB: 'Manitoba',
    NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador',
    NS: 'Nova Scotia',
    NT: 'Northwest Territories',
    NU: 'Nunavut',
    ON: 'Ontario',
    PE: 'Prince Edward Island',
    QC: 'Quebec',
    SK: 'Saskatchewan',
    YT: 'Yukon'
  };

  readonly stateByCode: Record<string, string> = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
    CT: 'Connecticut', DE: 'Delaware', DC: 'District Of Columbia', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky',
    LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
    NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
    OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico', VI: 'Virgin Islands'
  };

  readonly addressRequiredFields = [
    'address_type',
    'address_street',
    'address_locality',
    'address_region',
    'address_postal_code',
    'address_country'
  ];

  readonly addressDataFields = [
    'address_street',
    'address_extended',
    'address_community',
    'address_locality',
    'address_region',
    'address_postal_code',
    'address_country',
    'address_description'
  ];

  readonly personDataFields = [
    'contact_role',
    'contact_email',
    'contact_name',
    'phone_number',
    'phone_extension',
    'phone_description'
  ];

  contactForm: FormGroup;
  contactTypeFilterControl = new FormControl('');
  filteredContactTypes: string[] = [...this.typeListContact];
  addresses: AddressState[] = [];
  people: FormGroup[] = [];
  step: ContactCreatorStep = 'company';
  themeMode: ThemeMode = 'dark';
  saving = false;
  successMessage = '';
  errorMessage = '';
  createdContactUuid = '';

  private addressSearchTimers: Record<number, ReturnType<typeof setTimeout>> = {};

  constructor(
    private http: HttpClient,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private postgrid: PostgridService
  ) {
    this.contactForm = this.createContactForm();
    this.addAddress();
    this.addPerson();
  }

  ngOnInit(): void {
    this.initializeTheme();
    this.contactTypeFilterControl.valueChanges.subscribe((value) => {
      const search = this.clean(value).toLowerCase();
      this.filteredContactTypes = this.typeListContact.filter((type) => type.toLowerCase().includes(search));
    });
  }

  get activeStepIndex(): number {
    return this.steps.findIndex((step) => step.key === this.step);
  }

  get companyReady(): boolean {
    return this.contactForm.valid;
  }

  get addressCount(): number {
    return this.activeAddresses.length;
  }

  get peopleCount(): number {
    return this.activePeople.length;
  }

  get activeAddresses(): FormGroup[] {
    return this.addresses.filter((address) => this.hasAddressValue(address.form)).map((address) => address.form);
  }

  get activePeople(): FormGroup[] {
    return this.people.filter((person) => this.hasPersonValue(person));
  }

  get activeAddressValues(): any[] {
    return this.activeAddresses.map((address) => address.value);
  }

  get activePeopleValues(): any[] {
    return this.activePeople.map((person) => person.value);
  }

  get postgridStatusLabel(): string {
    const filled = this.addresses.filter((address) => address.fromPostgrid).length;
    if (filled > 0) {
      return `${filled} filled`;
    }

    return this.postgridLookupsEnabled ? 'Ready' : 'Manual';
  }

  get selectedContactTypeLabel(): string {
    const selectedTypes = this.contactForm.get('contact_type')?.value || [];
    return selectedTypes.length ? selectedTypes.join(', ') : 'Default';
  }

  get organizationLabel(): string {
    return this.contactForm.get('contact_organization')?.value || 'New contact';
  }

  createContactForm(): FormGroup {
    return this.formBuilder.group({
      contact_type: [['Default'], Validators.required],
      contact_organization: ['', Validators.required],
      contact_email: ['', Validators.email],
      contact_category: [''],
      contact_url: [''],
      active: ['True', Validators.required],
      contact_addresses: [[]],
      contact_name_prefix: [''],
      contact_name_given: [''],
      contact_name_family: [''],
      contact_nickname: [''],
      contact_note: [''],
      contact_time_zone: ['Eastern Time Zone'],
      us_dot_no: [''],
      contact_title: [''],
      contact_role: ['']
    });
  }

  createAddressForm(): FormGroup {
    return this.formBuilder.group({
      address_type: ['Main Office'],
      address_street: [''],
      address_extended: [''],
      address_community: [''],
      address_locality: [''],
      address_region: [''],
      address_postal_code: [''],
      address_country: [''],
      address_description: ['']
    });
  }

  createPersonForm(): FormGroup {
    return this.formBuilder.group({
      contact_role: [''],
      contact_email: ['', Validators.email],
      contact_name: [''],
      phone_type: ['Work'],
      phone_number: [''],
      phone_extension: [''],
      phone_description: [''],
      phone_primary: [false]
    });
  }

  addAddress(): void {
    this.addresses.push({
      form: this.createAddressForm(),
      suggestions: [],
      loading: false,
      status: 'idle',
      message: '',
      fromPostgrid: false
    });
  }

  removeAddress(index: number): void {
    if (this.addresses.length === 1) {
      this.clearAddress(index);
      return;
    }

    clearTimeout(this.addressSearchTimers[index]);
    this.addresses.splice(index, 1);
  }

  clearAddress(index: number): void {
    const address = this.addresses[index];
    if (!address) {
      return;
    }

    clearTimeout(this.addressSearchTimers[index]);
    address.form.reset({
      address_type: 'Main Office',
      address_street: '',
      address_extended: '',
      address_community: '',
      address_locality: '',
      address_region: '',
      address_postal_code: '',
      address_country: '',
      address_description: ''
    });
    address.suggestions = [];
    address.loading = false;
    address.status = 'idle';
    address.message = '';
    address.fromPostgrid = false;
    this.syncAddressValidators(address);
  }

  addPerson(): void {
    this.people.push(this.createPersonForm());
  }

  removePerson(index: number): void {
    if (this.people.length === 1) {
      this.clearPerson(index);
      return;
    }

    this.people.splice(index, 1);
  }

  clearPerson(index: number): void {
    const person = this.people[index];
    if (!person) {
      return;
    }

    person.reset({
      contact_role: '',
      contact_email: '',
      contact_name: '',
      phone_type: 'Work',
      phone_number: '',
      phone_extension: '',
      phone_description: '',
      phone_primary: false
    });
    this.syncPersonValidators(person);
  }

  setStep(step: ContactCreatorStep): void {
    this.step = step;
  }

  nextStep(): void {
    const nextIndex = Math.min(this.steps.length - 1, this.activeStepIndex + 1);
    this.step = this.steps[nextIndex].key;
  }

  prevStep(): void {
    const prevIndex = Math.max(0, this.activeStepIndex - 1);
    this.step = this.steps[prevIndex].key;
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  getAddressStates(country: string): string[] {
    if (country === 'Canada') {
      return Object.values(this.provinceByCode);
    }

    if (country === 'United States') {
      return Object.values(this.stateByCode);
    }

    return [];
  }

  onCountryChange(index: number): void {
    const address = this.addresses[index];
    if (!address) {
      return;
    }

    address.form.get('address_region')?.setValue('');
    address.fromPostgrid = false;
    this.markAddressManual(index);
    this.syncAddressValidators(address);
  }

  onAddressInput(index: number): void {
    const address = this.addresses[index];
    if (!address) {
      return;
    }

    address.fromPostgrid = false;
    this.markAddressManual(index);
    this.syncAddressValidators(address);

    clearTimeout(this.addressSearchTimers[index]);
    address.suggestions = [];

    if (!this.postgridLookupsEnabled) {
      return;
    }

    const street = this.clean(address.form.get('address_street')?.value);
    if (street.length < 3) {
      return;
    }

    this.addressSearchTimers[index] = setTimeout(() => this.searchPostgrid(index, street), 260);
  }

  onAddressBlur(index: number): void {
    const address = this.addresses[index];
    if (!address) {
      return;
    }

    this.markAddressManual(index);
    this.syncAddressValidators(address);
  }

  selectAddressSuggestion(event: MatAutocompleteSelectedEvent, index: number): void {
    const address = this.addresses[index];
    if (!address) {
      return;
    }

    const suggestion = address.suggestions.find((item) => item.label === event.option.value);
    if (!suggestion) {
      return;
    }

    const country = this.normalizeCountry(suggestion.country);
    const region = this.normalizeRegion(suggestion.region, country);

    address.form.patchValue({
      address_street: suggestion.street,
      address_extended: suggestion.extended,
      address_locality: suggestion.locality,
      address_region: region,
      address_postal_code: suggestion.postalCode,
      address_country: country
    });
    address.fromPostgrid = true;
    address.status = 'filled';
    address.message = 'PostGrid filled address.';
    address.suggestions = [];
    this.syncAddressValidators(address);
  }

  onPersonInput(index: number): void {
    const person = this.people[index];
    if (!person) {
      return;
    }

    this.syncPersonValidators(person);
  }

  submitNewContact(): void {
    this.successMessage = '';
    this.errorMessage = '';
    this.createdContactUuid = '';

    if (!this.validateBeforeSubmit()) {
      this.toastr.warning('Please review the highlighted fields.');
      return;
    }

    const typeIndexes = this.getSelectedContactTypeIndexes();
    const payload = this.buildContactPayload(typeIndexes[0]);

    this.saving = true;
    this.http.post<any>(this.contactUrl, payload).pipe(
      switchMap((response) => {
        const contactUuid = response?.details?.[0]?.uuid;
        if (!contactUuid) {
          throw new Error('The contact was created, but the response did not include a contact UUID.');
        }

        this.createdContactUuid = contactUuid;
        return this.http.post<any>(this.contactTypesUrl, {
          contact_uuid: contactUuid,
          contact_types: typeIndexes
        });
      }),
      finalize(() => {
        this.saving = false;
      })
    ).subscribe({
      next: () => {
        this.successMessage = `Created ${this.organizationLabel}.`;
        this.toastr.success('Contact created.');
        this.step = 'review';
      },
      error: (error) => {
        this.errorMessage = this.extractErrorMessage(error);
        this.toastr.error('Contact could not be created.');
      }
    });
  }

  controlInvalid(form: FormGroup, controlName: string): boolean {
    const control = form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  addressStatusClass(address: AddressState): string {
    return `cc-address-status--${address.status}`;
  }

  addressStatusIcon(address: AddressState): string {
    if (address.loading) {
      return 'fa-solid fa-rotate fa-spin';
    }

    if (address.status === 'filled') {
      return 'fa-solid fa-circle-check';
    }

    if (address.status === 'error') {
      return 'fa-solid fa-triangle-exclamation';
    }

    if (address.status === 'manual') {
      return 'fa-solid fa-pen';
    }

    return 'fa-solid fa-location-dot';
  }

  addressStatusLabel(address: AddressState): string {
    if (address.loading) {
      return 'Checking address';
    }

    if (address.message) {
      return address.message;
    }

    if (address.status === 'filled') {
      return 'Address filled';
    }

    if (address.status === 'manual') {
      return 'Manual entry';
    }

    if (address.status === 'error') {
      return 'Manual entry';
    }

    return 'Ready';
  }

  stepHasErrors(step: ContactCreatorStep): boolean {
    if (step === 'company') {
      return this.contactForm.invalid && this.formWasTouched(this.contactForm);
    }

    if (step === 'addresses') {
      return this.addresses.some((address) => address.form.invalid && this.formWasTouched(address.form));
    }

    if (step === 'people') {
      return this.people.some((person) => person.invalid && this.formWasTouched(person));
    }

    return !!this.errorMessage;
  }

  private initializeTheme(): void {
    const storedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (storedTheme === 'dark' || storedTheme === 'light') {
      this.themeMode = storedTheme;
      return;
    }

    this.themeMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private searchPostgrid(index: number, street: string): void {
    const address = this.addresses[index];
    if (!address) {
      return;
    }

    address.loading = true;
    this.postgrid.suggestAddress(street, this.getPostgridCountryFilter(address)).pipe(
      finalize(() => {
        address.loading = false;
      })
    ).subscribe({
      next: (suggestions) => {
        address.suggestions = suggestions.map((suggestion) => ({
          label: suggestion.label,
          street: suggestion.line1,
          extended: suggestion.line2,
          locality: suggestion.city,
          region: suggestion.provinceOrState,
          postalCode: suggestion.postalOrZip,
          country: suggestion.country,
          raw: suggestion.raw
        }));
        if (!address.suggestions.length && this.hasAddressValue(address.form)) {
          address.status = 'manual';
          address.message = 'Manual entry';
        }
      },
      error: () => {
        address.suggestions = [];
        address.status = 'error';
        address.message = 'PostGrid unavailable. Manual entry is still allowed.';
      }
    });
  }

  private getPostgridCountryFilter(address: AddressState): PostgridCountryFilter | undefined {
    const country = this.clean(address.form.get('address_country')?.value).toLowerCase();
    if (country === 'canada') {
      return 'CA';
    }

    if (country === 'united states') {
      return 'US';
    }

    return undefined;
  }

  private normalizeCountry(country: string): string {
    const value = this.clean(country).toUpperCase();
    if (value === 'CA' || value === 'CANADA') {
      return 'Canada';
    }

    if (value === 'US' || value === 'USA' || value === 'UNITED STATES') {
      return 'United States';
    }

    return this.clean(country);
  }

  private normalizeRegion(region: string, country: string): string {
    const code = this.clean(region).toUpperCase();
    if (country === 'Canada') {
      return this.provinceByCode[code] || this.clean(region);
    }

    if (country === 'United States') {
      return this.stateByCode[code] || this.clean(region);
    }

    return this.clean(region);
  }

  private validateBeforeSubmit(): boolean {
    this.contactForm.markAllAsTouched();
    this.contactForm.updateValueAndValidity();

    this.addresses.forEach((address) => {
      this.syncAddressValidators(address);
      address.form.markAllAsTouched();
    });

    this.people.forEach((person) => {
      this.syncPersonValidators(person);
      person.markAllAsTouched();
    });

    const addressesValid = this.addresses.every((address) => address.form.valid);
    const peopleValid = this.people.every((person) => person.valid);
    const typeIndexes = this.getSelectedContactTypeIndexes();

    if (!typeIndexes.length) {
      this.contactForm.get('contact_type')?.setErrors({ invalidType: true });
    }

    if (!this.contactForm.valid) {
      this.step = 'company';
      this.errorMessage = 'Company details need attention.';
      return false;
    }

    if (!addressesValid) {
      this.step = 'addresses';
      this.errorMessage = 'One or more addresses need attention.';
      return false;
    }

    if (!peopleValid) {
      this.step = 'people';
      this.errorMessage = 'One or more people need attention.';
      return false;
    }

    return true;
  }


  private getSelectedContactTypeIndexes(): number[] {
    const selectedTypes = this.contactForm.get('contact_type')?.value || [];
    return selectedTypes
      .map((type: string) => this.typeListContact.indexOf(type))
      .filter((index: number) => index >= 0);
  }

  private buildContactPayload(primaryTypeIndex: number): Record<string, unknown> {
    const payload = {
      ...this.contactForm.value,
      contact_type: primaryTypeIndex,
      contact_addresses: this.activeAddresses.map((address) => address.value),
      contact_phones: this.activePeople.map((person) => {
        const value = person.value;
        return {
          ...value,
          phone_primary: value.phone_primary ? '1' : '0'
        };
      })
    };

    return payload;
  }

  private syncAddressValidators(address: AddressState): void {
    const hasValue = this.hasAddressValue(address.form);
    this.addressRequiredFields.forEach((field) => {
      const control = address.form.get(field);
      control?.setValidators(hasValue ? [Validators.required] : []);
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private syncPersonValidators(person: FormGroup): void {
    const hasValue = this.hasPersonValue(person);
    const phoneNumber = this.clean(person.get('phone_number')?.value);

    person.get('contact_name')?.setValidators(hasValue ? [Validators.required] : []);
    person.get('phone_type')?.setValidators(phoneNumber ? [Validators.required] : []);
    person.get('contact_email')?.setValidators([Validators.email]);

    ['contact_name', 'phone_type', 'contact_email'].forEach((field) => {
      person.get(field)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private markAddressManual(index: number): void {
    const address = this.addresses[index];
    if (!address || address.fromPostgrid) {
      return;
    }

    if (this.hasAddressValue(address.form)) {
      address.status = 'manual';
      address.message = 'Manual entry';
    } else {
      address.status = 'idle';
      address.message = '';
    }
  }

  private hasAddressValue(form: FormGroup): boolean {
    return this.addressDataFields.some((field) => this.clean(form.get(field)?.value).length > 0);
  }

  private hasPersonValue(form: FormGroup): boolean {
    return this.personDataFields.some((field) => this.clean(form.get(field)?.value).length > 0);
  }

  private formWasTouched(form: FormGroup): boolean {
    return Object.values(form.controls).some((control: AbstractControl) => control.touched || control.dirty);
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message || error.message || 'The contact API returned an error.';
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'The contact could not be created.';
  }

  private clean(value: unknown): string {
    return String(value ?? '').trim();
  }
}
